import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import {
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const MarkdownHelp = () => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const markdownExamples = [
    {
      title: 'Headers',
      examples: [
        { syntax: '# H1', description: 'Main heading' },
        { syntax: '## H2', description: 'Sub heading' },
        { syntax: '### H3', description: 'Section heading' }
      ]
    },
    {
      title: 'Text Formatting',
      examples: [
        { syntax: '**bold**', description: 'Bold text' },
        { syntax: '*italic*', description: 'Italic text' },
        { syntax: '~~strikethrough~~', description: 'Strikethrough text' },
        { syntax: '`inline code`', description: 'Inline code' }
      ]
    },
    {
      title: 'Code Blocks',
      examples: [
        { syntax: '```\ncode block\n```', description: 'Code block' },
        { syntax: '```javascript\nconst x = 1;\n```', description: 'Syntax highlighted code' }
      ]
    },
    {
      title: 'Lists',
      examples: [
        { syntax: '- item 1\n- item 2', description: 'Unordered list' },
        { syntax: '1. item 1\n2. item 2', description: 'Ordered list' }
      ]
    },
    {
      title: 'Links & Images',
      examples: [
        { syntax: '[text](url)', description: 'Link' },
        { syntax: '![alt](url)', description: 'Image' }
      ]
    },
    {
      title: 'Blockquotes',
      examples: [
        { syntax: '> quoted text', description: 'Blockquote' }
      ]
    },
    {
      title: 'Tables',
      examples: [
        { syntax: '| Header | Header |\n|--------|--------|\n| Cell   | Cell   |', description: 'Table' }
      ]
    }
  ];

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          bgcolor: 'primary.main',
          color: 'white',
          width: 56,
          height: 56,
          boxShadow: 3,
          '&:hover': {
            bgcolor: 'primary.dark',
            transform: 'scale(1.1)',
          },
          transition: 'all 0.2s ease',
          zIndex: 1000,
        }}
        aria-label="Markdown help"
      >
        <InfoIcon />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            maxWidth: 400,
            maxHeight: 600,
            overflow: 'auto',
          }
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Markdown Syntax Guide
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Common Markdown syntaxes you can use in your answers:
          </Typography>

          {markdownExamples.map((section, sectionIndex) => (
            <Box key={sectionIndex} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                {section.title}
              </Typography>
              <List dense sx={{ py: 0 }}>
                {section.examples.map((example, exampleIndex) => (
                  <ListItem key={exampleIndex} sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label={example.syntax}
                            size="small"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              bgcolor: 'grey.100',
                              color: 'text.primary',
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {example.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {sectionIndex < markdownExamples.length - 1 && (
                <Divider sx={{ my: 1 }} />
              )}
            </Box>
          ))}

          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark" sx={{ fontSize: '0.8rem' }}>
              💡 <strong>Tip:</strong> Use the preview toggle in edit mode to see how your Markdown will look!
            </Typography>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default MarkdownHelp;
